import { configureStore } from '@reduxjs/toolkit';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Suspense, useEffect, useMemo } from 'react';
import { Provider } from 'react-redux';
import { BrowserRouter, MemoryRouter, Navigate, Route, Routes } from 'react-router-dom';

import { ExempleHomePage, ExempleVersionDataPage, MapPage } from './LazyRoutes';
import Path from './Path';
import MainLayout from './commons/layout/MainLayout';
import NotFoundPage from './commons/pages/NotFoundPage';
import ReducerRoot from './commons/redux/ReducerRoot';
import LanguageLayout from './commons/routing/LanguageLayout';
import LanguageRedirect from './commons/routing/LanguageRedirect';
import type RoutesType from './commons/types/RoutesType';
import i18n from './i18n';

const { EXEMPLE_HOME, EXEMPLE_VERSION, MAP } = Path;

/** Les constantes de Path sont absolues ("/home") ; les routes enfants de "/:lang" sont relatives. */
const relative = (path: string) => path.replace(/^\//, '');

/**
 * Toutes les pages sont sous "/:lang" (/fr/..., /en/...) : une URL par langue, indexable
 * séparément. Toute autre URL ("/", "/home") est redirigée vers la langue du visiteur.
 */
const RouterView = () => (
    <Routes>
        <Route path=":lang" element={<LanguageLayout />}>
            {/* La carte est la vue par défaut ; la vue standard est secondaire (bouton de bascule). */}
            <Route index element={<Navigate to={relative(MAP)} replace />} />
            <Route element={<MainLayout variant="map" />}>
                <Route path={relative(MAP)} element={<MapPage />} />
            </Route>
            <Route element={<MainLayout variant="standard" />}>
                <Route path={relative(EXEMPLE_HOME)} element={<ExempleHomePage />} />
                <Route path={relative(EXEMPLE_VERSION)} element={<ExempleVersionDataPage />} />
                <Route path="*" element={<NotFoundPage />} />
            </Route>
        </Route>
        <Route path="*" element={<LanguageRedirect />} />
    </Routes>
);

export default function Router({ configuration }: RoutesType) {
    const store = useMemo(() => configureStore({ reducer: ReducerRoot }), []);
    const queryClient = useMemo(() => new QueryClient(), []);

    // Les données renvoyées par l'API peuvent dépendre de la langue (Accept-Language) :
    // on les recharge quand l'utilisateur change de langue.
    useEffect(() => {
        const refetch = () => void queryClient.invalidateQueries();
        i18n.on('languageChanged', refetch);
        return () => i18n.off('languageChanged', refetch);
    }, [queryClient]);

    return (
        <Provider store={store}>
            <QueryClientProvider client={queryClient}>
                <Suspense fallback={<div />}>
                    {configuration?.silentRoutes ? (
                        <MemoryRouter>
                            <RouterView />
                        </MemoryRouter>
                    ) : (
                        <BrowserRouter>
                            <RouterView />
                        </BrowserRouter>
                    )}
                </Suspense>
            </QueryClientProvider>
        </Provider>
    );
}
